import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import DocumentTitle from 'react-document-title';
import { defaultTo, find, omit, propEq, reject } from 'ramda';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import * as editProspectHouseholdActions from './actions';
import {
  getAllAffordableRelationships,
  getAllApplicantTypes,
  getAllNameSuffixes,
  getAllPetTypes,
  getAllPropertyClasses,
  getAllRelationships,
} from '../App/actions';
import { EditProspectHouseholdForm } from '../../components/EditHouseholdForm';
import {
  generateInitialAdult,
  generateInitialMinor,
  generateInitialPet,
} from '../../components/EditHouseholdForm/utils';

import {
  getAffordableRelationshipOptions,
  getApplicablePropertyClassOptions,
  getApplicantTypeOptions,
  getMinorRelationshipOptions,
  getNameSuffixOptions,
  getPetTypeOptions,
  getRelationshipOptions,
  getSelectedPropertyClassType,
} from '../App/selectors';
import { getPropertyId, getProspectName } from './selectors';
import messages from './messages';

import type { GlobalState, Property } from '../App/types';
import type { Prospect } from '../ProspectProfile/types';
import { Spinner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { navigateToUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import {
  getPrimaryPhoneNumber,
  getPrimaryEmailAddress,
} from '../../utils/contact-helpers';

const getContactInfo = (adult, additionalPhoneAndEmailFieldsForLeadProfile) => {
  if (additionalPhoneAndEmailFieldsForLeadProfile) {
    return {
      phoneNumber: getPrimaryPhoneNumber(adult.phones, adult.phone),
      emailAddress: getPrimaryEmailAddress(adult.emails, adult.email),
    };
  }

  return {
    phoneNumber: adult.phone,
    emailAddress: adult.email,
  };
};

/**
 * Synchronizes the primary email and phone values in the arrays with the flat field values.
 * When the additionalPhoneAndEmailFieldsForLeadProfile flag is OFF, we need to ensure
 * that the primary email/phone in the arrays matches the values from the flat fields
 * (emailAddress/phoneNumber) to maintain data consistency.
 *
 * @param {Array} emails - Array of email objects with emailAddress and isPrimary properties
 * @param {Array} phones - Array of phone objects with phoneNumber and isPrimary properties
 * @param {string} primaryEmailAddress - The primary email address from flat field
 * @param {string} primaryPhoneNumber - The primary phone number from flat field
 * @returns {Object} Object containing synchronized emails and phones arrays
 */
const synchronizePrimaryContactArrays = (
  emails,
  phones,
  primaryEmailAddress,
  primaryPhoneNumber,
) => {
  // Synchronize emails array with primary email address
  let synchronizedEmails = emails || [];
  if (primaryEmailAddress && synchronizedEmails.length > 0) {
    synchronizedEmails = synchronizedEmails.map((email) => {
      if (email.isPrimary) {
        return {
          ...email,
          emailAddress: primaryEmailAddress,
        };
      }
      return email;
    });
  }

  // Synchronize phones array with primary phone number
  let synchronizedPhones = phones || [];
  if (primaryPhoneNumber && synchronizedPhones.length > 0) {
    synchronizedPhones = synchronizedPhones.map((phone) => {
      if (phone.isPrimary) {
        return {
          ...phone,
          phoneNumber: primaryPhoneNumber,
        };
      }
      return phone;
    });
  }

  return {
    emails: synchronizedEmails,
    phones: synchronizedPhones,
  };
};

type Option = {
  value: string,
  text: string,
  disabled: boolean,
};

type StateProps = {
  prospectId: string,
  prospect?: Prospect,
  prospectName: string,
  propertyId: string,
  primaryApplicantId: string,
  selectedProperty?: Property,
  propertyClassType: string,
  nameSuffixes: Array<Option>,
  propertyClasses: Array<Option>,
  relationships: Array<Option>,
  minorRelationships: Array<Option>,
  affordableRelationship: Array<Option>,
  applicantTypes: Array<Option>,
  petTypes: Array<Option & { petBreeds: Array<Option> }>,
  locale: string,
  isHouseholdSubmitting: boolean,
};
type Props = {
  intl: any,
  history: Object,
  flags: Object,
  actions: {
    clean: () => void,
    getOneProspect: (string) => void,
    editHousehold: (Prospect) => void,
    getAllNameSuffixes: () => void,
    getAllPropertyClasses: () => void,
    getAllApplicantTypes: () => void,
    getAllRelationships: () => void,
    getAllAffordableRelationships: () => void,
    getAllPetTypes: () => void,
  },
};

type State = {};

export class EditProspectHousehold extends Component<
  StateProps & Props,
  State,
> {
  componentWillMount() {
    this.props.actions.clean();
  }

  componentDidMount() {
    this.props.actions.getAllNameSuffixes();
    this.props.actions.getAllApplicantTypes();
    this.props.actions.getAllRelationships();
    this.props.actions.getAllAffordableRelationships();
    this.props.actions.getAllPropertyClasses();
    this.props.actions.getAllPetTypes();
    this.props.actions.getOneProspect(this.props.prospectId);
  }

  removeDefaultFromSelectFields = (values: any) => ({
    ...values,
    adults: values.adults.map((adult) => ({
      ...adult,
      suffixId: adult.suffixId === 'default' ? undefined : adult.suffixId,
      relationship:
        adult.relationship === 'default' ? undefined : adult.relationship,
    })),
    minors: values.minors.map((minor) => ({
      ...minor,
      suffixId: minor.suffixId === 'default' ? undefined : minor.suffixId,
      relationship:
        minor.relationship === 'default' ? undefined : minor.relationship,
    })),
    pets: values.pets.map((pet) => ({
      ...pet,
      petTypeId: pet.petTypeId === 'default' ? undefined : pet.petTypeId,
      breedId: pet.breedId === 'default' ? undefined : pet.breedId,
      weight: pet.weight === '' ? undefined : pet.weight,
      petTag: pet.petTag === '' ? undefined : pet.petTag,
    })),
  });
  handleCancel = () => {
    const route = `/prospect/${this.props.prospectId}`;
    navigateToUrlWithSelectedPropertyId(route);
  };
  getProspectFromValues = (values: any): ?Prospect => {
    const { prospect } = this.props;
    if (!prospect) {
      return undefined;
    }

    const isProspectFunc = propEq('id', values.primaryApplicantId);

    const updatedProspect = defaultTo({}, find(isProspectFunc, values.adults));

    const scrubbedProspect = omit(['portalUser'], prospect);

    // Get primary contact info based on feature flag
    const additionalPhoneAndEmailFieldsForLeadProfile =
      this.props.flags?.additionalPhoneAndEmailFieldsForLeadProfile;
    const primaryContactInfo = getContactInfo(
      updatedProspect,
      additionalPhoneAndEmailFieldsForLeadProfile,
    );

    // When feature flag is OFF, synchronize arrays with primary contact info to maintain consistency
    const prospectContactArrays = !additionalPhoneAndEmailFieldsForLeadProfile
      ? synchronizePrimaryContactArrays(
          updatedProspect.emails,
          updatedProspect.phones,
          primaryContactInfo.emailAddress,
          primaryContactInfo.phoneNumber,
        )
      : {
          emails: updatedProspect.emails,
          phones: updatedProspect.phones,
        };

    return {
      ...scrubbedProspect,
      primaryApplicantId: values.primaryApplicantId,
      firstName: updatedProspect.firstName,
      lastName: updatedProspect.lastName,
      middleName: updatedProspect.middleName,
      phoneNumber: primaryContactInfo.phoneNumber,
      emailAddress: primaryContactInfo.emailAddress,
      emails: prospectContactArrays.emails,
      phones: prospectContactArrays.phones,
      preferredName: updatedProspect.preferredName,
      suffixId: updatedProspect.suffixId,
      additionalOccupants: [
        ...reject(isProspectFunc, values.adults).map((adult) => {
          const adultContactInfo = getContactInfo(
            adult,
            this.props.flags?.additionalPhoneAndEmailFieldsForLeadProfile,
          );

          // When feature flag is OFF, synchronize arrays with primary contact info for additional occupants
          const adultContactArrays =
            !additionalPhoneAndEmailFieldsForLeadProfile
              ? synchronizePrimaryContactArrays(
                  adult.emails,
                  adult.phones,
                  adultContactInfo.emailAddress,
                  adultContactInfo.phoneNumber,
                )
              : {
                  emails: adult.emails,
                  phones: adult.phones,
                };

          return {
            id: adult.id,
            firstName: adult.firstName,
            middleName: adult.middleName,
            lastName: adult.lastName,
            emailAddress: adultContactInfo.emailAddress,
            phoneNumber: adultContactInfo.phoneNumber,
            emails: adultContactArrays.emails,
            phones: adultContactArrays.phones,
            type: 'Other Adult',
            preferredName: adult.preferredName,
            relationshipId: adult.relationship,
            suffixId: adult.suffixId,
          };
        }),
        ...values.minors.map((minor) => ({
          id: minor.id,
          firstName: minor.firstName,
          middleName: minor.middleName,
          lastName: minor.lastName,
          age: minor.age,
          type: 'Minor',
          preferredName: minor.preferredName,
          relationshipId: minor.relationship,
          suffixId: minor.suffixId,
          birthday: minor?.birthday,
        })),
      ],
      petOccupants: [
        ...values.pets.map((pet) => ({
          id: pet.id,
          name: pet.name,
          weight: pet.weight,
          serviceAnimal: pet.isServiceAnimal,
          petTypeId: pet.petTypeId,
          petBreedId: pet.breedId,
          petTag: pet.petTag,
        })),
      ],
    };
  };
  handleSubmit = (values: any) => {
    const prospectToUpdate = this.getProspectFromValues(
      this.removeDefaultFromSelectFields(values),
    );
    if (values.HOHChange?.previousHOHId && values.HOHChange?.newHOHId) {
      prospectToUpdate.HOHChange = values.HOHChange;
    }
    if (prospectToUpdate) {
      this.props.actions.editHousehold(prospectToUpdate);
    }
  };

  render() {
    const isLoading =
      this.props.nameSuffixesIsLoading ||
      this.props.propertyClassesIsLoading ||
      this.props.applicantTypesIsLoading ||
      this.props.relationshipsIsLoading ||
      this.props.petTypesIsLoading;

    const { prospect, prospectName, propertyId, prospectId } = this.props;

    const adults = generateInitialAdult(prospect);
    const minors = generateInitialMinor(prospect);
    const pets = generateInitialPet(prospect);

    const isConventionalProperty =
      this.props.propertyClassType === 'Conventional' ||
      prospect?.prospectPreferences?.unitType === 'conventional' ||
      (this.props.propertyClassType === 'Mixed' &&
        !prospect?.prospectPreferences?.unitType &&
        (!prospect?.prospectPreferences?.affordableProgramsArr ||
          prospect?.prospectPreferences?.affordableProgramsArr.length === 0));

    return (
      <>
        {isLoading ? (
          <Spinner
            sx={{
              height: '100vh',
              width: '100%',
              position: 'fixed',
              zIndex: 1,
            }}
          />
        ) : (
          <DocumentTitle title="Fortress - Edit Household">
            {prospect && (
              <EditProspectHouseholdForm
                initialValues={{
                  adults,
                  minors,
                  pets,
                  prospectId,
                  propertyId,
                  primaryApplicantId: prospect.primaryApplicantId,
                  unitTypeId: prospect.unitTypeId,
                }}
                pageTitle={`${this.props.intl.formatMessage(
                  messages.title,
                )} ${prospectName}`}
                suffixList={this.props.nameSuffixes}
                relationshipList={this.props.relationships}
                minorRelationshipList={this.props.minorRelationships}
                affordableRelationship={this.props.affordableRelationship}
                applicantTypeList={this.props.applicantTypes}
                propertyClassList={this.props.propertyClasses}
                handleCancel={this.handleCancel}
                onSubmit={this.handleSubmit}
                petTypeList={this.props.petTypes}
                primaryApplicantId={this.props.primaryApplicantId}
                handleEditHouseholdMember={() => {}}
                propertyClassType={this.props.propertyClassType}
                isConventionalProperty={isConventionalProperty}
                isHouseholdSubmitting={this.props.isHouseholdSubmitting}
              />
            )}
          </DocumentTitle>
        )}
      </>
    );
  }
}

export const mapStateToProps = (
  state: GlobalState,
  ownProps: Object,
): StateProps => {
  const { app, languageProvider, editProspectHousehold } = state;
  const prospect = editProspectHousehold.prospect;
  const isHouseholdSubmitting = editProspectHousehold.isHouseholdSubmitting;
  return {
    prospectId: ownProps.match.params.prospectId,
    prospect,
    selectedProperty: app.selectedProperty,
    propertyClassType: getSelectedPropertyClassType(state),
    nameSuffixes: getNameSuffixOptions(state),
    prospectName: getProspectName(state),
    propertyId: getPropertyId(state),
    propertyClasses: getApplicablePropertyClassOptions(state),
    relationships: getRelationshipOptions(state),
    minorRelationships: getMinorRelationshipOptions(state),
    affordableRelationship: getAffordableRelationshipOptions(state),
    applicantTypes: getApplicantTypeOptions(state),
    petTypes: getPetTypeOptions(state),
    locale: languageProvider.locale,
    primaryApplicantId: editProspectHousehold.primaryApplicantId,
    relationshipsIsLoading: state.app.relationshipsIsLoading,
    nameSuffixesIsLoading: state.app.nameSuffixesIsLoading,
    propertyClassesIsLoading: state.app.propertyClassesIsLoading,
    applicantTypesIsLoading: state.app.applicantTypesIsLoading,
    petTypesIsLoading: state.app.petTypesIsLoading,
    isHouseholdSubmitting,
  };
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators(
    {
      ...editProspectHouseholdActions,
      getAllNameSuffixes,
      getAllPropertyClasses,
      getAllApplicantTypes,
      getAllRelationships,
      getAllAffordableRelationships,
      getAllPetTypes,
    },
    dispatch,
  );
  return { actions };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(withLDConsumer()(EditProspectHousehold)));
